/**
 * Hotel PAN Details Thunks
 *
 * Redux thunks for PAN operations (add/remove).
 *
 * Trigger conditions:
 * - ADD: When 10-digit PAN is entered and PanCardV2 reports it as valid
 * - REMOVE: When PAN is cleared/removed
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { callPanDetailsApi, PanDetailsError } from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface AddPanParams {
  panNumber: string;
  isValidated: boolean;
}

interface PanOperationResult {
  panNumber: string | null;
  isValidated: boolean;
}

// ============================================
// Thunks
// ============================================

/**
 * Add PAN details - called when 10-digit valid PAN is entered
 */
export const addPanThunk = createAsyncThunk<
  PanOperationResult,
  AddPanParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/pan/add',
  async (params, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const userId = getUUID();

      logger.info('[PanThunk:add] Starting PAN add operation');

      const response = await callPanDetailsApi({
        sessionId,
        userId,
        actionType: 'hotel_pan_detail_add',
        panNumber: params.panNumber,
        isValidated: params.isValidated,
      });

      if (!response.success && response.error) {
        logger.warn('[PanThunk:add] API error:', response.error);
        return rejectWithValue(response.error.message);
      }

      logger.info('[PanThunk:add] Success', { isValidated: response.isValidated });

      return { panNumber: response.panNumber, isValidated: response.isValidated };
    } catch (error) {
      logger.error('[PanThunk:add] Failed:', error);
      if (error instanceof PanDetailsError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to add PAN details');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.hotels.bookingReview.isPanOperationLoading;
    },
  },
);

/**
 * Remove PAN details - called when PAN is cleared
 */
export const removePanThunk = createAsyncThunk<
  PanOperationResult,
  void,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/pan/remove',
  async (_, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const userId = getUUID();

      logger.info('[PanThunk:remove] Starting PAN remove operation');

      const response = await callPanDetailsApi({
        sessionId,
        userId,
        actionType: 'hotel_pan_detail_remove',
        panNumber: '',
        isValidated: false,
      });

      if (!response.success && response.error) {
        logger.warn('[PanThunk:remove] API error:', response.error);
        return rejectWithValue(response.error.message);
      }

      logger.info('[PanThunk:remove] Success');

      return { panNumber: '', isValidated: false };
    } catch (error) {
      logger.error('[PanThunk:remove] Failed:', error);
      if (error instanceof PanDetailsError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to remove PAN details');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.hotels.bookingReview.isPanOperationLoading;
    },
  },
);
